/**
 * CollapsibleSidePanel — shared shell for left- and right-docked collapsible panels.
 *
 * Handles:
 *  - Fixed positioning anchored to the left or right edge of the viewport.
 *  - A small toggle tab that sticks out from the panel edge.
 *  - Collapsed / expanded transitions via CSS transform.
 *  - Forwarding a ref to the outer wrapper for consumers that need DOM measurements.
 *
 * Both InsightsPanel (left) and TriagePanel (right) use this component so the
 * structural logic is maintained in a single place.
 */
import React, { forwardRef, useCallback } from 'react';

export interface CollapsibleSidePanelProps {
    /** Which side of the viewport the panel is docked to. */
    side: 'left' | 'right';
    /** When true the panel is translated off-screen, showing only the toggle tab. */
    isCollapsed: boolean;
    /** Called when the toggle button or the panel body (while collapsed) is clicked. */
    onToggleCollapse: () => void;
    /**
     * Optional guard executed before auto-opening when the collapsed panel is clicked.
     * If it returns `true` the open action is suppressed (e.g. after project switch).
     */
    shouldSuppressOpen?: () => boolean;
    /** Panel body content rendered below the toggle button. */
    children: React.ReactNode;
    /** Additional CSS class applied to the outer wrapper div. */
    className?: string;
    /** Accessible label for the toggle button. */
    ariaLabelOpen?: string;
    ariaLabelClose?: string;
}

/**
 * A collapsible side panel shell.  The ref is forwarded to the outer div so
 * parent components can measure it (e.g. for drag-to-reorder card logic).
 */
const CollapsibleSidePanel = forwardRef<HTMLDivElement, CollapsibleSidePanelProps>(
    function CollapsibleSidePanel(
        {
            side,
            isCollapsed,
            onToggleCollapse,
            shouldSuppressOpen,
            children,
            className = '',
            ariaLabelOpen = 'Open panel',
            ariaLabelClose = 'Close panel',
        },
        ref,
    ) {
        const handlePanelClick = useCallback(
            (e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                // Only respond to clicks when the panel is collapsed (to open it)
                if (!isCollapsed) return;
                if (shouldSuppressOpen?.()) return;
                onToggleCollapse();
            },
            [isCollapsed, onToggleCollapse, shouldSuppressOpen],
        );

        const handleToggleButtonClick = useCallback(
            (e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onToggleCollapse();
            },
            [onToggleCollapse],
        );

        const sideClass = side === 'right' ? 'collapsible-panel--right' : '';
        const collapsedClass = isCollapsed ? 'collapsible-panel--collapsed' : '';

        return (
            <div
                ref={ref}
                className={`collapsible-panel ${sideClass} ${collapsedClass} ${className}`.trim()}
                onClick={handlePanelClick}
            >
                <button
                    className='collapsible-panel__toggle'
                    onClick={handleToggleButtonClick}
                    aria-label={isCollapsed ? ariaLabelOpen : ariaLabelClose}
                >
                    <div className='collapsible-panel__line' />
                </button>

                {children}
            </div>
        );
    },
);

export default CollapsibleSidePanel;
